import type CodeMirror from 'codemirror';
import type { HTMLAttributes, ReactElement, ForwardedRef } from 'react';

import { forwardRef, memo, useMemo } from 'react';

import Spinner from '@ui/Spinner';

import './readme-syntax.scss';
import './style.scss';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const syntaxHighlighter = typeof window !== 'undefined' ? require('@readme/syntax-highlighter/dist').default : () => {};

// @fixme: Ugh. The types for CodeMirror.{Editor,Doc} appear to be missing
// attributes?!
export interface CMEditor extends CodeMirror.Editor {
  doc: CodeMirror.Doc & {
    children: [
      {
        lines: [
          {
            text: string;
          },
        ];
      },
    ];
    sel: {
      ranges: [
        {
          anchor: {
            ch: number;
            line: number;
          };
          head: {
            ch: number;
            line: number;
          };
        },
      ];
    };
    size: number;
  };
}

export type CodeSnippetProps = HTMLAttributes<HTMLDivElement> & {
  className?: string;
  code: string;
  isLoading?: boolean;
  editorProps?: {
    editorDidMount?: (codeEditor: ReturnType<typeof CodeMirror>) => void;
    // source: https://github.com/scniro/react-codemirror2/blob/673a66822d6e56486075ea6336d258172f6361f7/src/index.tsx#L51
    onChange?: (editor: CodeMirror.Editor, data: CodeMirror.EditorChange, value: string) => void;
    onKeyDown?: (codeMirrorEditor: CMEditor, event: KeyboardEvent) => void;
  };
  footer?: ReactElement;
  header?: ReactElement;
  language: string;
  options?: {
    customTheme?: 'material-palenight' | 'neo' | 'readme-syntax' | 'tomorrow-night';
    dark?: boolean;
    editable?: boolean;
    fixedGutter?: boolean;
    foldGutter?: boolean;
    highlightMode?: boolean;
    inputStyle?: 'contenteditable' | 'textarea';
    lineWrapping?: boolean;
    ranges?: { ch: number; line: number }[];
    readOnly?: boolean;
    tokenizeVariables?: boolean;
  };
};

const CodeSnippet = memo(
  forwardRef(function CodeSnippet(
    { className, header, code, language, options, editorProps, footer, isLoading = false, ...args }: CodeSnippetProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const snippet = useMemo(
      () => syntaxHighlighter(code, language, options, editorProps),
      [code, language, options, editorProps],
    );

    if (isLoading) {
      return (
        <div {...args} ref={ref} className={className || ''} role="none">
          <pre className="CodeSnippet">
            <div
              className="CodeEditor cm-s-material-palenight"
              style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '155px' }}
            >
              <Spinner />
            </div>
          </pre>
        </div>
      );
    }

    return (
      <div {...args} ref={ref} className={className || ''} role="none">
        {header}
        <pre className="CodeSnippet">{snippet}</pre>
        {footer}
      </div>
    );
  }),
);

export default CodeSnippet;
